import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { useUser } from "../context/UserContext";
import { useSound } from "../context/SoundContext";
import { getMatches, markSeen } from "../lib/api";
import MatchCard from "../components/MatchCard";
import MatchReveal from "../components/MatchReveal";
import PageWrapper from "./PageWrapper";

export default function MatchesView() {
  const { currentUserId } = useUser();
  const { playMatchSound } = useSound();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revealQueue, setRevealQueue] = useState(null);
  const [revealIndex, setRevealIndex] = useState(0);

  const load = useCallback(() => {
    if (!currentUserId) return;
    setLoading(true);
    getMatches(currentUserId)
      .then(setMatches)
      .finally(() => setLoading(false));
  }, [currentUserId]);

  useEffect(() => {
    load();
  }, [load]);

  const unseen = matches.filter((m) => !m.seen);
  const revealed = matches.filter((m) => m.seen);

  const startReveal = () => {
    if (unseen.length === 0) return;
    setRevealQueue(unseen);
    setRevealIndex(0);
    // Directly inside the click gesture so Safari/iOS allows playback.
    playMatchSound();
  };

  const markCurrentSeen = async (item) => {
    try {
      await markSeen(item.id, currentUserId);
    } catch (e) {
      /* A failed seen marker must not block the reveal experience. */
    }
  };

  const next = async () => {
    const item = revealQueue[revealIndex];
    // Play before awaiting the network call: keeps audio tied to the user's tap on mobile Safari.
    playMatchSound();
    await markCurrentSeen(item);
    setRevealIndex((i) => i + 1);
  };

  const closeReveal = async () => {
    const item = revealQueue[revealIndex];
    await markCurrentSeen(item);
    setRevealQueue(null);
    setRevealIndex(0);
    load();
  };

  return (
    <PageWrapper>
      <div className="matches-view" data-testid="matches-view">
        <p className="page-eyebrow">À deux</p>
        <h1 className="page-title">Nos matchs</h1>

        {loading ? (
          <div className="center-note">Chargement…</div>
        ) : (
          <>
            {unseen.length > 0 && (
              <motion.div
                className="reveal-prompt"
                data-testid="reveal-prompt"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="reveal-prompt-glow" />
                <Sparkles size={26} strokeWidth={1.4} />
                <p className="reveal-prompt-text">
                  {unseen.length} nouveau{unseen.length > 1 ? "x" : ""} match
                  {unseen.length > 1 ? "s" : ""}
                </p>
                <button className="btn-primary" data-testid="discover-btn" onClick={startReveal}>
                  Révéler nos matchs
                </button>
              </motion.div>
            )}

            {revealed.length > 0 ? (
              <div className="matches-grid">
                {revealed.map((m) => (
                  <MatchCard key={m.id} item={m} />
                ))}
              </div>
            ) : (
              unseen.length === 0 && (
                <div className="empty-state" data-testid="matches-empty">
                  <h2 className="empty-title">Pas encore de match</h2>
                  <p className="empty-text">
                    Répondez à des cartes&nbsp;— quand vos réponses s'accordent, un match
                    apparaît ici.
                  </p>
                </div>
              )
            )}
          </>
        )}
      </div>

      {revealQueue && (
        <MatchReveal
          item={revealQueue[revealIndex]}
          index={revealIndex}
          total={revealQueue.length}
          onNext={next}
          onClose={closeReveal}
        />
      )}
    </PageWrapper>
  );
}
